import { useEffect, useState } from "react";
import { useDebounce } from "use-debounce";
import {
  CommonModelScopesPageParams,
  fetchCommonModelScopesPage,
} from "../../../../../../api-client/categories/CommonModelTogglesAPIClient";
import { APICategory } from "../../../../../../models/Entities";
import { showErrorToast } from "../../../../../shared/Toasts";
import { DEFAULT_SCOPES_ORDERING, SCOPES_ACTION_STATE_ANY } from "../constants";

export interface ScopesToolbar {
  search: string;
  setSearch: (value: string) => void;
  ordering: string;
  setOrdering: (value: string) => void;
  /** Read scope filter: "any" (no filter) | "enabled" | "optional" | "disabled". */
  read: string;
  setRead: (value: string) => void;
  /** Write scope filter: "any" (no filter) | "enabled" | "optional" | "disabled". */
  write: string;
  setWrite: (value: string) => void;
  /**
   * Server-ordered model names to display, or null when the toolbar is in its default state (no
   * search/filter and default sort) — in which case the caller renders every model in its usual order.
   */
  visibleModelNames: string[] | null;
  /** True while a filter/search fetch is in flight, and stays true if that fetch fails, so the table
   *  keeps showing a loading state rather than a stale/incorrect list on error. */
  resultsLoading: boolean;
  /** Re-run the server fetch (call after a save so the filtered list reflects the new saved state). */
  refetch: () => void;
}

/**
 * Toolbar state (sort/filter/search) for the Common Model scopes page. The actual filtering/sorting/
 * searching is done server-side: whenever the toolbar is non-default this fetches the matching,
 * ordered model names, which the page uses to decide which loaded rows to render and in what order.
 * The full model set stays loaded for editing/saving, so this never affects the batched-edit flow.
 */
export const useScopesToolbar = (category: APICategory): ScopesToolbar => {
  // state
  const [search, setSearch] = useState("");
  const [ordering, setOrdering] = useState(DEFAULT_SCOPES_ORDERING);
  const [read, setRead] = useState(SCOPES_ACTION_STATE_ANY);
  const [write, setWrite] = useState(SCOPES_ACTION_STATE_ANY);
  const [visibleModelNames, setVisibleModelNames] = useState<string[] | null>(null);
  const [resultsLoading, setResultsLoading] = useState(false);
  // Bumped by refetch() to force the fetch effect to re-run (e.g. after a save) even when the
  // filter inputs are unchanged.
  const [reloadToken, setReloadToken] = useState(0);

  const [debouncedSearch] = useDebounce(search, 300);

  // derived state
  const isFiltering =
    !!debouncedSearch ||
    read !== SCOPES_ACTION_STATE_ANY ||
    write !== SCOPES_ACTION_STATE_ANY ||
    ordering !== DEFAULT_SCOPES_ORDERING;

  // Reset the toolbar when the category (route) changes.
  useEffect(() => {
    setSearch("");
    setOrdering(DEFAULT_SCOPES_ORDERING);
    setRead(SCOPES_ACTION_STATE_ANY);
    setWrite(SCOPES_ACTION_STATE_ANY);
  }, [category]);

  // Fetch server-ordered/filtered model names while non-default; clear the override when default so the
  // page renders all models in their normal order. Ignore a response if inputs changed mid-flight.
  useEffect(() => {
    if (!isFiltering) {
      setVisibleModelNames(null);
      setResultsLoading(false);
      return;
    }
    let active = true;
    setResultsLoading(true);
    const params: CommonModelScopesPageParams = {
      search: debouncedSearch || undefined,
      read: read !== SCOPES_ACTION_STATE_ANY ? read : undefined,
      write: write !== SCOPES_ACTION_STATE_ANY ? write : undefined,
      ordering,
    };
    fetchCommonModelScopesPage(category, params).then((result) => {
      if (!active) return;
      if (result.status === "success") {
        setVisibleModelNames(result.data.map((model) => model.name));
        setResultsLoading(false);
      } else {
        // Surface the failure and stay in the loading state so the table never shows a stale or
        // incorrect list that silently doesn't match the applied filter.
        showErrorToast("Couldn't apply filters, please try again");
      }
    });
    return () => {
      active = false;
    };
  }, [category, debouncedSearch, ordering, read, write, isFiltering, reloadToken]);

  return {
    search,
    setSearch,
    ordering,
    setOrdering,
    read,
    setRead,
    write,
    setWrite,
    visibleModelNames,
    resultsLoading,
    refetch: () => setReloadToken((token) => token + 1),
  };
};
